import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Building2, Users, CheckCircle, XCircle, Clock, Loader2,
  ShieldCheck, ShieldOff, Gift,
} from 'lucide-react';

const OrganisationManagement = () => {
  const { api } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchOrganisations(); }, []);

  const fetchOrganisations = async () => {
    try {
      const res = await api.get('/organisations');
      setOrganisations(res.data);
    } catch (error) {
      console.error('Failed to fetch organisations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (orgId, approved) => {
    try {
      await api.post(`/organisations/${orgId}/approve?approved=${approved}`);
      toast.success(`Organisation ${approved ? 'approved' : 'rejected'}`);
      fetchOrganisations();
    } catch { toast.error('Failed to update organisation'); }
  };

  const handleToggleFreeAccess = async (orgId, enabled) => {
    try {
      await api.post(`/organisations/${orgId}/free-access?enabled=${enabled}`);
      toast.success(`Free access ${enabled ? 'enabled' : 'disabled'}`);
      fetchOrganisations();
    } catch { toast.error('Failed to toggle free access'); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return { className: 'bg-[#f8fafc] text-[#2a3e5d]', icon: CheckCircle };
      case 'pending': return { className: 'bg-[#F4A261]/15 text-[#F4A261]', icon: Clock };
      case 'suspended': return { className: 'bg-red-100 text-red-500', icon: XCircle };
      default: return { className: 'bg-gray-100 text-gray-500', icon: Clock };
    }
  };

  const filteredOrgs = filter === 'all'
    ? organisations
    : organisations.filter(o => o.status === filter);

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="org-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Organisations</h1>
            <p className="text-sm text-[#1e293b]/60 mt-1">
              {organisations.length} total &middot; {organisations.filter(o => o.status === 'pending').length} pending approval
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'suspended'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-[#20acf6] hover:bg-[#3bb8f7] text-white' : 'border-[#f8fafc]'}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && organisations.filter(o => o.status === 'pending').length > 0 && (
                <Badge className="ml-1 bg-[#F4A261] text-white text-xs">{organisations.filter(o => o.status === 'pending').length}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Organisation List */}
        {filteredOrgs.length === 0 ? (
          <Card className="border-[#f8fafc]">
            <CardContent className="p-12 text-center">
              <Building2 className="h-12 w-12 text-[#02d1d4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1e293b]">No organisations found</h3>
              <p className="text-sm text-[#1e293b]/60">{filter !== 'all' ? 'Try changing the filter' : 'No organisations have registered yet'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredOrgs.map((org) => {
              const statusInfo = getStatusBadge(org.status);
              const StatusIcon = statusInfo.icon;
              return (
                <Card key={org.id} className="border-[#f8fafc] hover:shadow-sm transition-shadow" data-testid={`org-row-${org.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-[#02d1d4]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#1e293b]">{org.name}</h3>
                            {org.is_free_access && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1" data-testid={`free-access-badge-${org.id}`}>
                                <Gift className="h-3 w-3" /> FREE ACCESS
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-[#1e293b]/50 mt-0.5">
                            <span>{org.contact_email}</span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" /> {org.team_member_count} members
                            </span>
                            {org.billing_cycle && (
                              <span className="capitalize">{org.billing_cycle} billing</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Free Access Toggle */}
                        <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`free-access-toggle-${org.id}`}>
                          <Label className="text-xs text-slate-500">Free Access</Label>
                          <Switch
                            checked={!!org.is_free_access}
                            onCheckedChange={(checked) => handleToggleFreeAccess(org.id, checked)}
                          />
                        </div>

                        <Badge className={statusInfo.className}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {org.status}
                        </Badge>
                        {org.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(org.id, true)}
                              className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                              data-testid={`approve-${org.id}`}
                            >
                              <ShieldCheck className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApprove(org.id, false)}
                              className="border-[#F4A261] text-[#F4A261] hover:bg-[#F4A261]/10"
                              data-testid={`reject-${org.id}`}
                            >
                              <ShieldOff className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {org.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(org.id, false)}
                            className="border-[#F4A261] text-[#F4A261] hover:bg-[#F4A261]/10"
                            data-testid={`suspend-${org.id}`}
                          >
                            <ShieldOff className="h-4 w-4 mr-1" /> Suspend
                          </Button>
                        )}
                        {org.status === 'suspended' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(org.id, true)}
                            className="border-[#20acf6] text-[#20acf6] hover:bg-[#f8fafc]"
                            data-testid={`reactivate-${org.id}`}
                          >
                            <ShieldCheck className="h-4 w-4 mr-1" /> Reactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrganisationManagement;
